import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import {
  adminApi,
  BrandingProvider,
  ConfirmProvider,
  adminBasename,
  createQueryClient,
  getHostMode,
  initSupabaseConfig,
  preloadBranding,
  renderBootError,
} from '@l2u/web-shared';
import { AdminAuthProvider } from './lib/auth/admin.tsx';
import { App } from './App.tsx';
import { ToastProvider } from './components/Toast.tsx';
import './index.css';

// Each SPA app owns its own QueryClient cache instance.
const queryClient = createQueryClient();

const root = document.getElementById('root');
if (!root) throw new Error('Root element missing');

// Phase 3b boot gate: resolve which Supabase project (and auth pool) this
// origin uses BEFORE anything can construct a client. Accepted cost: if the
// API is down, the boot-error screen renders instead of the login page —
// config is no longer baked into the bundle. `vite dev` short-circuits to the
// VITE_* env values inside initSupabaseConfig, so local dev works API-free.
//
// The basename is read HERE, inside the resolved callback, because it must be
// fixed before the first render and getHostMode() throws until the gate has
// resolved. In `legacy` mode the router re-adds the `/admin` prefix to every
// link and strips it from every matched location, so nothing else in the app
// has to know which mode it is running.
//
// The tenant's brand is fetched ALONGSIDE the config, and the first render
// waits for both, so a tenant on purple never sees one frame of the product's
// green first (index.css declares green as the --brand-* default). It adds no
// serial round trip: the two requests are independent. preloadBranding never
// rejects — a failed or slow brand renders with the product fallback.
Promise.all([initSupabaseConfig(), preloadBranding()]).then(
  ([, preloaded]) => {
    createRoot(root).render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrandingProvider api={adminApi} preloaded={preloaded}>
            <BrowserRouter basename={adminBasename(getHostMode())}>
              <AdminAuthProvider>
                <ConfirmProvider>
                  <ToastProvider>
                    <App />
                  </ToastProvider>
                </ConfirmProvider>
              </AdminAuthProvider>
            </BrowserRouter>
          </BrandingProvider>
        </QueryClientProvider>
      </StrictMode>,
    );
  },
  (err: unknown) => renderBootError(root, err),
);
